import { APIPath } from '../constant'
import { BaseService } from './base'

// Asset maintenance tasks (EAMM-005 / EAUDITOR-407). A maintenance task is a task whose inspection is the work
// checklist, linked to an asset; the backend creates the work audit as the assignee.
class Maintenance {
    types() {
        return BaseService.get(APIPath.maintenanceTypes)
    }

    create(data) {
        return BaseService.post(APIPath.maintenanceTask, data)
    }

    get(taskId) {
        return BaseService.get(`${APIPath.maintenanceTask}/${taskId}`)
    }

    update(taskId, data) {
        return BaseService.put(`${APIPath.maintenanceTask}/${taskId}`, data)
    }

    // data: { Kind: 'source' | 'pre' | 'post', Remove, TaskId, SourceAuditId, SourceQuestionId, SourceTicketId }
    link(taskId, data) {
        return BaseService.post(`${APIPath.maintenanceTask}/${taskId}/Link`, data)
    }

    // Create from a finding / ticket (EAUDITOR-408): { auditId, questionId, answerId } or { ticketId }
    fromFinding({ auditId, questionId, answerId, ticketId } = {}) {
        const q = new URLSearchParams()
        if (auditId) q.set('auditId', auditId)
        if (questionId) q.set('questionId', questionId)
        if (answerId) q.set('answerId', answerId)
        if (ticketId) q.set('ticketId', ticketId)
        return BaseService.get(`${APIPath.maintenanceRoot}FromFinding?${q.toString()}`)
    }

    byTicket(ticketId) {
        return BaseService.get(`${APIPath.maintenanceRoot}Ticket/${ticketId}/Tasks`)
    }

    // Technician workflow (EAUDITOR-409)
    accept(taskId) {
        return BaseService.post(`${APIPath.maintenanceTask}/${taskId}/Accept`, {})
    }

    // data: { WorkPerformed, PartsUsed, LabourHours, Cost, DowntimeHours, CompletionNotes } - the whole card
    saveCompletion(taskId, data) {
        return BaseService.put(`${APIPath.maintenanceTask}/${taskId}/Completion`, data)
    }

    summary() {
        return BaseService.get(`${APIPath.maintenanceRoot}Summary`)
    }

    // filter: mine | overdue | awaiting_my_approval | awaiting_my_verification
    myTasks(filter) {
        return BaseService.get(`${APIPath.maintenanceRoot}Tasks?filter=${encodeURIComponent(filter || 'mine')}`)
    }

    // Manager approval (EAUDITOR-410): approve = { Notes, AssetStatus }, reject = { Reason } (required)
    approve(taskId, data) {
        return BaseService.post(`${APIPath.maintenanceTask}/${taskId}/Approve`, data || {})
    }

    reject(taskId, data) {
        return BaseService.post(`${APIPath.maintenanceTask}/${taskId}/Reject`, data || {})
    }

    byAsset(assetId) {
        return BaseService.get(`${APIPath.maintenanceAsset}${assetId}/Tasks`)
    }

    byAudit(auditId) {
        return BaseService.get(`${APIPath.maintenanceAudit}${auditId}/Tasks`)
    }

    // Schedules (EAUDITOR-405 / 406)
    schedulesByAsset(assetId) {
        return BaseService.get(`${APIPath.maintenanceAsset}${assetId}/Schedules`)
    }

    getSchedule(scheduleId) {
        return BaseService.get(`${APIPath.maintenanceSchedule}/${scheduleId}`)
    }

    createSchedule(data) {
        return BaseService.post(APIPath.maintenanceSchedule, data)
    }

    updateSchedule(scheduleId, data) {
        return BaseService.put(`${APIPath.maintenanceSchedule}/${scheduleId}`, data)
    }

    removeSchedule(scheduleId) {
        return BaseService.remove(`${APIPath.maintenanceSchedule}/${scheduleId}`)
    }

    pauseSchedule(scheduleId) {
        return BaseService.post(`${APIPath.maintenanceSchedule}/${scheduleId}/Pause`, {})
    }

    resumeSchedule(scheduleId) {
        return BaseService.post(`${APIPath.maintenanceSchedule}/${scheduleId}/Resume`, {})
    }

    runScheduleNow(scheduleId) {
        return BaseService.post(`${APIPath.maintenanceSchedule}/${scheduleId}/RunNow`, {})
    }

    retryOccurrence(occurrenceId) {
        return BaseService.post(`${APIPath.maintenanceOccurrence}${occurrenceId}/Retry`, {})
    }

    // Inspections of an asset (search mode, rows carry TaskId) - used to pick pre / post / source links.
    assetAudits(companyId, assetId) {
        return BaseService.post(APIPath.assetManagerAuditListByAsset, {
            CompanyId: companyId,
            AssetId: assetId,
            PageNumber: 1,
            PageSize: 100,
            SortBy: 'CreatedOn',
            SortOrder: 'DESC',
        })
    }
}

const MaintenanceService = Object.freeze(new Maintenance())
export { MaintenanceService }
